import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User,
  Mail,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Users,
  Shield,
  BarChart3,
  Plus,
  Search,
  Filter,
  Package,
  DollarSign
} from 'lucide-react';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Mock data for users
  useEffect(() => {
    const mockPendingUsers = [
      {
        id: 'google_1234567890',
        name: 'Nguyễn Văn A',
        email: 'user1@gmail.com',
        avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        status: 'pending',
        createdAt: '2024-01-15T10:30:00Z',
        role: 'user'
      },
      {
        id: 'google_1234567891',
        name: 'Trần Thị B',
        email: 'user2@gmail.com',
        avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        status: 'pending',
        createdAt: '2024-01-15T11:45:00Z',
        role: 'user'
      },
      {
        id: 'google_1234567892',
        name: 'Lê Văn C',
        email: 'user3@gmail.com',
        avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        status: 'pending',
        createdAt: '2024-01-15T14:20:00Z',
        role: 'user'
      }
    ];

    const mockAllUsers = [
      ...mockPendingUsers,
      {
        id: 'google_1234567893',
        name: 'Phạm Văn D',
        email: 'user4@gmail.com',
        avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        status: 'approved',
        createdAt: '2024-01-10T09:15:00Z',
        role: 'user',
        approvedAt: '2024-01-12T14:30:00Z'
      },
      {
        id: 'google_1234567894',
        name: 'Hoàng Thị E',
        email: 'user5@gmail.com',
        avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        status: 'approved',
        createdAt: '2024-01-08T16:45:00Z',
        role: 'user',
        approvedAt: '2024-01-09T10:20:00Z'
      },
      {
        id: 'google_1234567895',
        name: 'Admin User',
        email: 'admin@mainsys.com',
        avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
        status: 'approved',
        createdAt: '2024-01-01T00:00:00Z',
        role: 'admin',
        approvedAt: '2024-01-01T00:00:00Z'
      }
    ];

    setTimeout(() => {
      setPendingUsers(mockPendingUsers);
      setAllUsers(mockAllUsers);
      setLoading(false);
    }, 1000);
  }, []);

  const handleApprove = async (userId) => {
    setPendingUsers(prev => prev.filter(user => user.id !== userId));
    setAllUsers(prev => prev.map(user => 
      user.id === userId ? { ...user, status: 'approved', approvedAt: new Date().toISOString() } : user
    ));
    alert('Đã phê duyệt tài khoản thành công!');
  };

  const handleReject = async (userId) => {
    setPendingUsers(prev => prev.filter(user => user.id !== userId));
    setAllUsers(prev => prev.filter(user => user.id !== userId));
    alert('Đã từ chối tài khoản!');
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa user này?')) {
      setAllUsers(prev => prev.filter(user => user.id !== userId));
      alert('Đã xóa user thành công!');
    }
  };

  const handleUpdateUser = (updatedUser) => {
    setAllUsers(prev => prev.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ));
    setShowUserModal(false);
    setSelectedUser(null);
    alert('Đã cập nhật user thành công!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const stats = [
    {
      name: 'Tổng sản phẩm',
      value: '1,234',
      change: '+12%',
      changeType: 'positive',
      icon: Package
    },
    {
      name: 'Doanh thu',
      value: '$45,678',
      change: '+8%',
      changeType: 'positive',
      icon: DollarSign
    },
    {
      name: 'Đơn hàng',
      value: '567',
      change: '+15%',
      changeType: 'positive',
      icon: Package
    },
    {
      name: 'Khách hàng',
      value: '890',
      change: '+5%',
      changeType: 'positive',
      icon: Users
    }
  ];

  const userStats = {
    totalUsers: allUsers.length,
    pendingUsers: pendingUsers.length,
    approvedUsers: allUsers.filter(u => u.status === 'approved').length,
    adminUsers: allUsers.filter(u => u.role === 'admin').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600">Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.role === 'admin' ? 'Admin' : 'User'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BarChart3 className="inline-block w-4 h-4 mr-2" />
                Tổng quan
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pending'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Clock className="inline-block w-4 h-4 mr-2" />
                Chờ duyệt ({pendingUsers.length})
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Users className="inline-block w-4 h-4 mr-2" />
                Quản lý User
              </button>
            </nav>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {stats.map((stat) => {
                    const Icon = stat.icon;
                    return (
                      <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <Icon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-4 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  {stat.name}
                                </dt>
                                <dd className="flex items-baseline">
                                  <div className="text-2xl font-semibold text-gray-900">
                                    {stat.value}
                                  </div>
                                  <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {stat.change}
                                  </div>
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* User Statistics */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Users className="h-6 w-6 text-blue-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Tổng User</dt>
                            <dd className="text-lg font-medium text-gray-900">{userStats.totalUsers}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Clock className="h-6 w-6 text-yellow-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Chờ duyệt</dt>
                            <dd className="text-lg font-medium text-gray-900">{userStats.pendingUsers}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-6 w-6 text-green-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Đã duyệt</dt>
                            <dd className="text-lg font-medium text-gray-900">{userStats.approvedUsers}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Shield className="h-6 w-6 text-purple-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Admin</dt>
                            <dd className="text-lg font-medium text-gray-900">{userStats.adminUsers}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Hoạt động gần đây</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Package className="h-4 w-4 text-blue-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            Sản phẩm mới được thêm
                          </p>
                          <p className="text-sm text-gray-500">
                            iPhone 15 Pro đã được thêm vào danh sách sản phẩm
                          </p>
                        </div>
                        <div className="text-sm text-gray-500">2 giờ trước</div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                            <Users className="h-4 w-4 text-green-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            Người dùng mới đăng ký
                          </p>
                          <p className="text-sm text-gray-500">
                            Nguyễn Văn A đã tạo tài khoản mới
                          </p>
                        </div>
                        <div className="text-sm text-gray-500">4 giờ trước</div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center">
                            <DollarSign className="h-4 w-4 text-yellow-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            Đơn hàng mới
                          </p>
                          <p className="text-sm text-gray-500">
                            Đơn hàng #12345 với giá trị $1,299 đã được tạo
                          </p>
                        </div>
                        <div className="text-sm text-gray-500">6 giờ trước</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pending Users Tab */}
            {activeTab === 'pending' && (
              <div>
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Không có tài khoản chờ duyệt</h3>
                    <p className="text-gray-500">Tất cả tài khoản đã được xử lý.</p>
                  </div>
                ) : (
                  <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                      {pendingUsers.map((pendingUser) => (
                        <li key={pendingUser.id}>
                          <div className="px-4 py-4 sm:px-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  {pendingUser.avatar ? (
                                    <img
                                      src={pendingUser.avatar}
                                      alt={pendingUser.name}
                                      className="h-12 w-12 rounded-full"
                                    />
                                  ) : (
                                    <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                                      <User className="h-6 w-6 text-gray-500" />
                                    </div>
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className="flex items-center">
                                    <p className="text-sm font-medium text-gray-900">{pendingUser.name}</p>
                                    <div className="ml-2 flex items-center">
                                      <Clock className="h-4 w-4 text-yellow-500" />
                                      <span className="ml-1 text-xs text-yellow-600">Chờ duyệt</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center mt-1">
                                    <Mail className="h-4 w-4 text-gray-400 mr-1" />
                                    <p className="text-sm text-gray-500">{pendingUser.email}</p>
                                  </div>
                                  <div className="flex items-center mt-1">
                                    <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                    <p className="text-xs text-gray-500">
                                      Đăng ký: {formatDate(pendingUser.createdAt)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleApprove(pendingUser.id)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Phê duyệt
                                </button>
                                <button
                                  onClick={() => handleReject(pendingUser.id)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Từ chối
                                </button>
                                <button className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                  <Eye className="h-4 w-4 mr-1" />
                                  Xem chi tiết
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Users Management Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                {/* Search and Filter */}
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Tìm kiếm user..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">Tất cả vai trò</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Users List */}
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Danh sách User ({filteredUsers.length})
                    </h3>
                  </div>
                  <ul className="divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <li key={user.id}>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                {user.avatar ? (
                                  <img
                                    src={user.avatar}
                                    alt={user.name}
                                    className="h-12 w-12 rounded-full"
                                  />
                                ) : (
                                  <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                                    <User className="h-6 w-6 text-gray-500" />
                                  </div>
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="flex items-center">
                                  <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {user.role === 'admin' ? 'Admin' : 'User'}
                                  </span>
                                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    user.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {user.status === 'approved' ? 'Đã duyệt' : 'Chờ duyệt'}
                                  </span>
                                </div>
                                <div className="flex items-center mt-1">
                                  <Mail className="h-4 w-4 text-gray-400 mr-1" />
                                  <p className="text-sm text-gray-500">{user.email}</p>
                                </div>
                                <div className="flex items-center mt-1">
                                  <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                  <p className="text-xs text-gray-500">
                                    Đăng ký: {formatDate(user.createdAt)}
                                    {user.approvedAt && (
                                      <span className="ml-2">• Duyệt: {formatDate(user.approvedAt)}</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditUser(user)}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Sửa
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Xóa
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard; 
